import React, { useEffect, useRef, useState } from 'react';

interface CventRegistrationProps {
    embedId: string;
    onRegistrationCompleted: (data: CventRegistrationData) => void;
}

export interface CventRegistrationData {
    message: string;
    fName: string;
    lName: string;
    attendeeId: string;
    email: string;
    firstName: string;
    lastName: string;
}

interface CventRegistrationEvent extends MessageEvent {
    data: CventRegistrationData;
}

const CventRegistration = ({ embedId, onRegistrationCompleted }: CventRegistrationProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const handleMessage = (event: CventRegistrationEvent) => {
        // This is a security check to make sure the message is from Cvent
        if (event.origin !== 'https://web.cvent.com') {
            return;
        }

        if (event.type === 'message' && event.data.message === 'registrationCompleted') {
            onRegistrationCompleted(event.data);
        }
    };

    window.addEventListener('message', handleMessage);

    return () => {
        window.removeEventListener('message', handleMessage);
    };
}, []);

  useEffect(() => {
    const div = ref.current;
    const scriptSrc = `https://web.cvent.com/event_guest/v1/embed/${embedId}.js`;
    const scriptExists = document.querySelector(`script[src="${scriptSrc}"]`);
    if (!scriptExists && div) {
      const script = document.createElement('script');
      script.src = scriptSrc;
      script.async = true;

      script.onload = () => {
        const iframe = div.querySelector('iframe');
        if (iframe) {
          let src = iframe.getAttribute('src');
          if (src) {
            // Remove originalUrl query param from iframe src as it causes 403
            src = src.replace(/([&?]originalUrl=[^&]+)/, '');
            iframe.setAttribute('src', src);
            iframe.onload = () => setIsLoading(false);
          } else {
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      };

      script.onerror = () => {
        setHasError(true);
        setIsLoading(false);
      };

      div.appendChild(script);

      return () => {
        while (div.firstChild) {
          div.removeChild(div.firstChild);
        }
      };
    } else {
      setIsLoading(false);
    }
  }, [embedId, ref]);

  return (
    <div ref={ref} data-cvt-embed>
      {isLoading && <p>Loading...</p>}
      {hasError && <p>Failed to load the registration form.</p>}
    </div>
  );
};

export default CventRegistration;
